import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/client';
import { usersApi, type CallerView } from '../../api/client';
import { campaignsApi, type Campaign, type CampaignMember } from '../../api/campaigns';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormField,
  RowActions,
  Section,
  Select,
  StatusChip,
  Table,
} from '../../components';
import { useI18n } from '../../i18n';
import { useSession } from '../../auth/session-context';

/**
 * Freigabe von Callern für eine Kampagne.
 * Nur freigegebene Caller sehen die Datensätze.
 */
export function MembersTab({ campaign }: { campaign: Campaign }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [members, setMembers] = useState<CampaignMember[]>([]);
  const [callers, setCallers] = useState<CallerView[]>([]);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [memberResponse, callerResponse] = await Promise.all([
        campaignsApi.listMembers(campaign.id),
        usersApi.listCallers(),
      ]);
      setMembers(memberResponse.members);
      setCallers(callerResponse.callers);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    }
  }, [campaign.id, handleApiError, tError]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await action();
      await load();
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  const verfuegbar = callers.filter(
    (caller) => caller.isActive && !members.some((member) => member.userId === caller.id),
  );

  return (
    <Card>
      <Section title={t('campaign.callers.title')} description={t('campaign.callers.hint')}>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {message ? <Alert tone="info">{message}</Alert> : null}

        <div className="form form-inline">
          <FormField label={t('campaign.callers.add')}>
            <Select
              value={selected}
              options={[
                { value: '', label: '–' },
                ...verfuegbar.map((caller) => ({
                  value: caller.id,
                  label: caller.username ?? caller.email ?? caller.id,
                })),
              ]}
              onChange={setSelected}
            />
          </FormField>
          <Button
            variant="primary"
            disabled={busy || selected === ''}
            onClick={() =>
              void run(async () => {
                await campaignsApi.addMember(campaign.id, selected);
                setSelected('');
                setMessage(null);
              })
            }
          >
            {t('campaign.callers.add')}
          </Button>
        </div>

        {members.length === 0 ? (
          <EmptyState title={t('campaign.callers.empty')} description={t('campaign.callers.hint')} />
        ) : (
          <Table
            label={t('campaign.callers.title')}
            headers={[t('callers.username'), t('campaign.callers.assignedContacts'), '']}
          >
            {members.map((member) => (
              <tr key={member.userId}>
                <td>
                  <strong>{member.username ?? member.email}</strong>
                </td>
                <td>
                  {campaign.campaignMode === 'assigned' ? (
                    <StatusChip tone="info">{member.assignedContacts}</StatusChip>
                  ) : (
                    <span className="field-hint">–</span>
                  )}
                </td>
                <RowActions>
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const result = await campaignsApi.removeMember(campaign.id, member.userId);
                        setMessage(
                          result.unassignedContacts > 0
                            ? t('campaign.callers.removed', { count: result.unassignedContacts })
                            : null,
                        );
                      })
                    }
                  >
                    {t('campaign.callers.remove')}
                  </Button>
                </RowActions>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </Card>
  );
}
